import { store } from "@/store/stores"
import Vue from "vue"
import userService from "@/services/userService"
import { buildTrainingLiveState, createTrainingSessionId } from "./trainingLiveState"

class TrainingLiveService {
  constructor() {
    this.ready = false
    this.queue = Promise.resolve()
    this.pendingResumeSessionId = null
    this.viewState = Vue.observable({ watch: null })
    this.handleState = (event) => {
      const state = event?.detail
      if (!state || state.userId !== String(userService.getUserId() || "")) return
      if (state.owner !== "watch") { this.viewState.watch = null; return }
      const current = this.viewState.watch
      if (current?.clientSessionId === state.clientSessionId && current.revision > state.revision) return
      this.viewState.watch = state
    }
    this.handleReady = () => this.bootstrap()
    this.handleForeground = () => this.bootstrap({ foreground: true })
    this.handleOpen = (event) => this.open(event?.detail?.clientSessionId, event?.detail?.action)
    this.handleVisible = () => { if (document.visibilityState === "visible") return this.bootstrap() }
  }

  async start(router) {
    if (this.unsubscribe) return
    this.router = router
    await store.restored
    this.unsubscribe = store.subscribe((mutation) => {
      if (mutation.type === "userStore/setUser") this.bootstrap()
      if (!mutation.type.startsWith("runWorkoutStore/") || mutation.type.endsWith("SET_LIVE_SESSION")) return
      if (this.ready && mutation.type.endsWith("RESET_WORKOUT_STATE") && this.lastPhoneSessionId && this.lastPhoneSessionId !== this.savedSessionId) {
        this.enqueue("watchCompanionWorkoutCommand", { action: "discard", clientSessionId: this.lastPhoneSessionId })
        this.lastPhoneSessionId = null
      }
      if (mutation.type.endsWith("SET_LIVE_DISPLAY_SETTINGS")) this.bootstrap()
      else this.schedule()
    })
    window.addEventListener("flutterInAppWebViewPlatformReady", this.handleReady)
    window.addEventListener("trainingLiveOpen", this.handleOpen)
    window.addEventListener("trainingLiveStateChanged", this.handleState)
    window.addEventListener("trainingLiveForeground", this.handleForeground)
    document.addEventListener("visibilitychange", this.handleVisible)
    await this.bootstrap()
  }

  stop() {
    this.unsubscribe?.()
    this.unsubscribe = null
    this.ready = false
    this.pendingResumeSessionId = null
    window.removeEventListener("flutterInAppWebViewPlatformReady", this.handleReady)
    window.removeEventListener("trainingLiveOpen", this.handleOpen)
    window.removeEventListener("trainingLiveStateChanged", this.handleState)
    window.removeEventListener("trainingLiveForeground", this.handleForeground)
    document.removeEventListener("visibilitychange", this.handleVisible)
  }

  async call(method, payload = {}) {
    const bridge = window.flutter_inappwebview
    if (!bridge?.callHandler) return null
    let timeout
    try {
      return await Promise.race([
        Promise.resolve(bridge.callHandler(method, payload)),
        new Promise((resolve) => { timeout = setTimeout(() => resolve({ supported: false, accepted: false }), 2500) }),
      ])
    } finally {
      clearTimeout(timeout)
    }
  }

  ensureSession() {
    const state = store.state.runWorkoutStore
    const userId = String(userService.getUserId() || "")
    if (!state.activeSplit || !userId) return null
    if (state.liveSession?.userId && state.liveSession.userId !== userId) return null
    if (!state.liveSession?.clientSessionId) {
      store.commit("runWorkoutStore/SET_LIVE_SESSION", {
        clientSessionId: state.watchCompanion?.clientSessionId || createTrainingSessionId(),
        userId, startedAt: Date.parse(state.workoutStartTime) || Date.now(), revision: 0, phase: "active", syncState: "local",
      })
    }
    this.lastPhoneSessionId = state.liveSession.clientSessionId
    return state.liveSession
  }

  async bootstrap({ foreground = document.visibilityState === "visible" } = {}) {
    // An old native app / Android / browser simply has no compatible adapter.
    if (!window.flutter_inappwebview?.callHandler) return
    try {
      const userId = String(userService.getUserId() || "")
      const settings = store.state.runWorkoutStore.liveDisplaySettings || {}
      const response = await this.call("trainingLiveConfigure", {
        userId, enabled: settings.enabled !== false,
        showHeartRate: settings.showHeartRate === true, showEnergy: settings.showEnergy === true,
      })
      this.ready = response?.supported === true
      if (!this.ready) return
      this.lastSignature = null
      const live = this.ensureSession()
      if (response.snapshot) this.handleState({ detail: response.snapshot })
      if (live && response.snapshot?.clientSessionId === live.clientSessionId) {
        store.commit("runWorkoutStore/SET_LIVE_SESSION", { revision: Math.max(live.revision || 0, response.snapshot.revision || 0) })
      }
      if (foreground && this.router?.currentRoute?.path === "/workout/run/split" && live?.phase !== "ending"
        && store.state.runWorkoutStore.liveDisplaySettings?.enabled !== false) {
        this.pendingResumeSessionId = live?.clientSessionId || null
      }
      await this.call("trainingLiveReconcile", { clientSessionId: live?.clientSessionId || null })
      this.schedule()
      if (response.pendingOpen) await this.open(response.pendingOpen, response.pendingOpenAction)
    } catch (_) {
      // Optional system display never blocks the workout or an older wrapper.
      this.ready = false
    }
  }

  schedule() {
    // Coalesce a synchronous Vuex action (set + superset advance) after commits.
    if (this.scheduled) return
    this.scheduled = true
    Promise.resolve().then(() => {
      this.scheduled = false
      const live = this.ensureSession()
      if (!this.ready) return
      const payload = live && buildTrainingLiveState(store.state.runWorkoutStore, userService.getUserId())
      if (!payload) {
        this.pendingResumeSessionId = null
        this.enqueue("trainingLiveReconcile", { clientSessionId: null })
        return
      }
      const timeBasis = payload.phase === "ending" ? payload.elapsedSeconds : Math.round(payload.elapsedAnchorAt - payload.elapsedSeconds * 1000)
      const signature = JSON.stringify({ ...payload, revision: 0, updatedAt: 0, elapsedSeconds: timeBasis, elapsedAnchorAt: 0 })
      const resumeActivity = this.pendingResumeSessionId === payload.clientSessionId && payload.phase !== "ending"
        && store.state.runWorkoutStore.liveDisplaySettings?.enabled !== false
      this.pendingResumeSessionId = null
      if (signature === this.lastSignature && !resumeActivity) return
      this.lastSignature = signature
      payload.revision = (live.revision || 0) + 1
      store.commit("runWorkoutStore/SET_LIVE_SESSION", { revision: payload.revision })
      if (this.lastPublishedSessionId !== payload.clientSessionId) {
        this.enqueue("trainingLiveReconcile", { clientSessionId: payload.clientSessionId })
        this.lastPublishedSessionId = payload.clientSessionId
      }
      // One foreground recovery request, attached to the latest state. It must
      // neither become part of the saved workout nor retrigger on every timer tick.
      this.enqueue("trainingLiveUpdate", resumeActivity ? { ...payload, resumeActivity: true } : payload)
    })
  }

  enqueue(method, payload) {
    this.queue = this.queue.then(async () => {
      const result = await this.call(method, payload)
      if (result?.accepted === false) this.lastSignature = null
    }).catch(() => { this.lastSignature = null })
    return this.queue
  }

  ending() {
    const live = this.ensureSession()
    if (!live || live.phase === "ending") return
    const startedAt = Date.parse(store.state.runWorkoutStore.workoutStartTime) || live.startedAt
    store.commit("runWorkoutStore/SET_LIVE_SESSION", { phase: "ending", elapsedSeconds: Math.max(0, (Date.now() - startedAt) / 1000), syncState: "pending" })
    this.lastSignature = null
    this.schedule()
  }

  async saved() {
    const live = this.ensureSession()
    if (live && this.ready) await this.enqueue("trainingLiveSaved", { clientSessionId: live.clientSessionId })
    this.savedSessionId = live?.clientSessionId
    this.lastPhoneSessionId = null
  }

  resume() {
    const live = this.ensureSession()
    if (!live) return
    if (live.phase === "ending") store.commit("runWorkoutStore/SET_LIVE_SESSION", { phase: "active", syncState: "local" })
    this.pendingResumeSessionId = live.clientSessionId
    this.lastSignature = null
    if (this.ready) this.schedule()
    else this.bootstrap()
  }

  async clear() {
    this.ready = false
    this.viewState.watch = null
    this.lastSignature = null
    this.pendingResumeSessionId = null
    store.commit("runWorkoutStore/RESET_WORKOUT_STATE")
    await this.enqueue("trainingLiveConfigure", { userId: "", enabled: false, showHeartRate: false, showEnergy: false })
  }

  async open(sessionId, action) {
    if (!sessionId || !userService.getUserId() || !this.router) return
    const live = this.ensureSession()
    // A Watch-owned session stays on the Watch; do not create a second workout.
    const path = live?.clientSessionId === sessionId
      ? live.phase === "ending" ? "/workout/summary" : "/workout/run/split"
      : "/workout/hub"
    if (path === "/workout/run/split" && action === "enter-set") {
      const query = this.router.currentRoute.path === path ? this.router.currentRoute.query || {} : {}
      await this.router.push({ path, query: { ...query, liveSet: sessionId } }).catch(() => {})
    } else if (this.router.currentRoute.path !== path) await this.router.push(path).catch(() => {})
    if (path === "/workout/hub") {
      await Vue.nextTick()
      document.getElementById("watch-live-training")?.scrollIntoView({ block: "center" })
    }
    await this.call("trainingLiveOpenHandled", { clientSessionId: sessionId })
  }
}

export default new TrainingLiveService()
